import type { FC } from 'react';

import { useAppState } from '../../AppStateProvider';
import type { LockVariant } from '../../domain/gainAccessView';
import { GAIN_ACCESS_VARIANT_PROPS, NT_PURCHASE_PAGE_LINK } from '../../domain/gainAccessView';
import { useNTStatusQuery } from '../../features/user/useNTStatusQuery';
import { useRequestAccessMutation } from '../../features/user/useRequestAccessMutation';
import { StyledContainerWithNav } from '../../pages/styles';
import { appStore } from '../../services/appStore';
import { AssetsService } from '../../services/AssetsService';
import { translate } from '../../services/i18n';
import { useDispatchTrackEventWithLocation } from '../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { IconButton } from '../Button/IconButton';

import {
  StyledGainAccessThanksTitle,
  StyledGainAccessDescription,
  StyledGainAccessContainer,
  StyledGainAccessThanksImage,
  StyledGainAccessImage,
  StyledGainAccessTitle,
} from './styles';

export const GainAccessView: FC<{ variant: LockVariant }> = ({ variant }) => {
  const variantProps = GAIN_ACCESS_VARIANT_PROPS[variant];
  const { hasRequestedAccess } = useAppState();
  const hasRequestedAccessKey = Boolean(variantProps.storageKey && hasRequestedAccess?.[variantProps.storageKey]);

  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();

  const requestAccessMutation = useRequestAccessMutation();
  const { data: NTStatus } = useNTStatusQuery();

  const trackContactUs = () => dispatchTrackEventWithLocation('hooks', 'contact us', 'limit reached');

  const handleClick = () => {
    if (!variantProps.feature || !variantProps.storageKey) {
      return;
    }

    if (['company-daily-limit-reached', 'company-monthly-limit-reached'].includes(variant)) {
      trackContactUs();
    } else if (
      ['insights-limit-reached-trial', 'insights-limit-reached-after-trial', 'insights-limit-reached-paid'].includes(
        variant
      )
    ) {
      if (NTStatus?.isAllowed) {
        dispatchTrackEventWithLocation('hooks', 'upgrade package', 'limit reached/request more insights');
      } else {
        trackContactUs();
      }
    }
    if (!NTStatus?.isAllowed) {
      requestAccessMutation.mutate(variantProps.feature);
      appStore.setHasRequestedAccess({ [variantProps.storageKey]: true });
    }
  };

  return (
    <StyledContainerWithNav>
      <StyledGainAccessContainer>
        {hasRequestedAccessKey ? (
          <>
            <StyledGainAccessThanksImage src={AssetsService.assetUrl('/images/balloons.svg')} />
            <StyledGainAccessThanksTitle>{translate('access_requested.title')}</StyledGainAccessThanksTitle>
            <StyledGainAccessDescription>{translate('access_requested.text')}</StyledGainAccessDescription>
          </>
        ) : (
          <>
            <StyledGainAccessImage src={AssetsService.assetUrl(variantProps.image)} />
            <StyledGainAccessTitle>{translate(variantProps.title)}</StyledGainAccessTitle>
            <StyledGainAccessDescription>{translate(variantProps.description)}</StyledGainAccessDescription>
            {variant !== 'company-monthly-limit-reached-nt' ? (
              <IconButton variant="trial" iconName="sparkles-gray" iconSize="xs" onClick={handleClick}>
                {NTStatus?.isAllowed ? (
                  <a href={NT_PURCHASE_PAGE_LINK} target="_blank" rel="noreferrer">
                    {translate('upgrade_package.button')}
                  </a>
                ) : (
                  translate(variantProps.button || 'common.contact_us_to_upgrade')
                )}
              </IconButton>
            ) : null}
          </>
        )}
      </StyledGainAccessContainer>
    </StyledContainerWithNav>
  );
};
